import { GlassButton } from "src/components/GlassButtons";
import { GlassInputGroup, GlassInputGroupTitle, GlassTextInputTransformer } from "src/components/GlassInput";
import { GlassInfo } from "src/components/MitGlassComponents";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { ViewButtonText } from "src/propertyInfoTemplates/PropertyComponents";
import { Feature } from "src/states/ApplicationStateFeatures";
import { JobActionType, JobsContext, JobType, JobTypeUserAccessRights, getMyAccessRights, InputFieldType } from "./JobState";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { LayerInfo, LayerType } from "src/common/managers/Types";
import { message } from "src/components/Message";
import { Cookies, Localization, SessionContext, SettingsFixedPriorities, SettingsManager } from "@viamap/viamap2-common";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import React from "react";
import { MapitStateContext } from "src/states/MapitState";
import { SelectColumn, SelectLayer } from "src/components/CommonInputs";
import { createPortal } from "react-dom";
import { SettingsEditor, keyValueObject } from "src/components/SettingsEditor";
import { JobServiceInterface } from "./JobServiceInterface";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { InputFieldSpec } from "./JobServiceInterface";
import { ADModalBody, ADModalFooter } from "src/componentsUtils/AdvancedDragModal";
import { JobInputParamsServer } from "./JobInputParamsServer";

type Props = {
   preSelectedJob?: JobType;
   onCreate?: () => void;
};

export function JobInstantiateDialog(props: Props) {
   const settingsFixedPriorities = 70;

   const { state: jobsState, dispatch: jobsDispatch } = useContext(JobsContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState } = React.useContext(MapitStateContext);

   const [availableLayers, setAvailableLayers] = useState<any>([]);

   // const [instantiationSpecs, setInstantiationSpecs] = useState<InputFieldSpec[]>([]);
   const [instantiationParameters, setInstantiationParameters] = useState<{}>({});
   const [projectId, setProjectId] = useState<string>();
   const [showSettings, setShowSettings] = useState<boolean>(false);
   const [jobInterfaceInstance, setJobInterfaceInstance] = useState<JobServiceInterface>();
   const [inputFieldSpec, setInputFieldSpec] = useState<InputFieldSpec[]>([]);
   const projRef = useRef<any>()

   useEffect(() => {
      if (jobsState.selectedJobType) {
         let jobType = jobsState.availableJobTypesList?.find((a) => a.id === jobsState.selectedJobType?.id);
         if (jobType) {
            setProjectId(jobType.id);

            let jobInterface = new JobServiceInterface({
               userRef: sessionState.userRef,
               userName: sessionState.userName,
               organizationId: sessionState.customerRef,
               languageCode: Localization.getLanguage() as "da" | "en"
            });
            setJobInterfaceInstance(jobInterface);
            setInputFieldSpec(jobInterface.getJobTypeInputFields(jobType.serverTypeName!));
         }
      }
   }, [jobsState.selectedJobType])

   useEffect(() => {
      // Keep a list of available layers with Dgunr property
      let relevantLayers = Object.values(mapitState.layers).filter((layerInfo: LayerInfo) => {
         return (layerInfo.type === LayerType.GeoJSON_Point)
      }
      );
      let relevantLayerKeys = relevantLayers.map((lr) => { return lr.layerId }).sort();
      let availableLayerKeys = availableLayers.map((lr) => { return lr.layerId }).sort();
      // only update if there are new layers - not on any (e.g. data or styling change)
      if (!(relevantLayerKeys + "" === availableLayerKeys + "")) {
         setAvailableLayers(relevantLayers);
      }
   }, [mapitState.layers]);

   useEffect(() => {
      let layerId = getInstantiationParameter("selectedLayerId_From");
      if (layerId) {
         let layerInfo = mapitState.layers[layerId];
         let options = layerInfo?.propertiesInGeoJson || [];
         setInstantiationParameter("selectedLayerId_From_Options", options);
      } else {
         setInstantiationParameter("selectedLayerId_From_Options", []);
      }
   }, [instantiationParameters["selectedLayerId_From"]])
   useEffect(() => {
      let layerId = getInstantiationParameter("selectedLayerId_To");
      if (layerId) {
         let layerInfo = mapitState.layers[layerId];
         let options = layerInfo?.propertiesInGeoJson || [];
         setInstantiationParameter("selectedLayerId_To_Options", options);
      } else {
         setInstantiationParameter("selectedLayerId_To_Options", []);
      }
   }, [instantiationParameters["selectedLayerId_To"]])



   function runInstantiate(settings: any) {

      if (!getInstantiationParameter("selectedLayerId_From") || !getInstantiationParameter("selectedLayerId_To") || !projectId) {
         message.error("Please select both layers and a project before starting a new job.");
         return;
      }

      let layerId = getInstantiationParameter("selectedLayerId_From");
      let li: LayerInfo = mapitState.layers[layerId];
      let geoJsonOrigins = li.geoJson;
      let layerId2 = getInstantiationParameter("selectedLayerId_To");
      let li2: LayerInfo = mapitState.layers[layerId2];
      let geoJsonDestinations = li2.geoJson;

      let destinationsSum = Object.keys(instantiationParameters).reduce((sum, key) => {
         if (key.startsWith("selectedColumnId_From_DestId")) {
            let val = instantiationParameters[key] || "";
            if (val) {
               return sum + 1;
            }
         }
         return sum;
      }, 0);

      let jobSize = geoJsonOrigins.features.length * destinationsSum;
      let limit = SettingsManager.getSystemSetting("jobs.maxJobSize", 1000, true);
      if (jobSize > limit) {
         message.error(Localization.getFormattedText("Job size is too large. Please reduce the number of origins or destinations. Limit is {limit}, current size is {jobSize}", { limit: limit, jobSize: jobSize }));
         return;
      }

      let jobInterface = new JobServiceInterface({
         userRef: sessionState.userRef,
         userName: sessionState.userName,
         organizationId: sessionState.customerRef,
         languageCode: Localization.getLanguage() as "da" | "en"
      });
      let { success, errorMessages } = jobInterface.validateSettingsForJob(jobsState.selectedJobType?.serverTypeName ?? "Error no job type name", settings);
      if (!success) {
         message.error("Error validating settings: " + errorMessages!.join(", "));
         return;
      }
      let filteredInstantiationParameters: any = Object.keys(instantiationParameters).reduce((result: any, val: string) => {
         if (!val.startsWith("selectedLayerId")) {
            return { ...result, [val]: instantiationParameters[val] }
         } else {
            return result;
         }
      }, {});
      jobInterface.CreateJob(
         jobsState.selectedJobType?.serverTypeName ?? "Error no job type name",
         instantiationParameters["instanceName"] ?? "Error no job name",
      ).then(async (createResult) => {
         try {
            let originFileName = "origins.json";
            let destinationFileName = "destinations.json";
            let combinedSettings = { ...settings, ...filteredInstantiationParameters, originFileName: originFileName, destinationFileName: destinationFileName };
            await jobInterface.JobUploadJson(createResult.jobId, originFileName, JSON.stringify(geoJsonOrigins));
            await jobInterface.JobUploadJson(createResult.jobId, destinationFileName, JSON.stringify(geoJsonDestinations));
            await jobInterface.JobUploadSettings(createResult.jobId, JSON.stringify(combinedSettings));
            await jobInterface.subscribe(createResult.jobId, sessionState.userRef, Localization.getLanguage() as "da" | "en");
            jobInterface.StartJob(createResult.jobId).then((startResult) => {
               // Localization.getFormattedText("Job started: {name}", {name: startResult.name});
               message.info(Localization.getFormattedText("Job started: {name}", { name: startResult.name }));
               props.onCreate && props.onCreate();
            })
         } catch (error: any) {
            console.log("Got error " + error);
            message.error("Error load and start: " + (error.message || error));
         }
      }).catch((error) => {
         console.log("Got error " + error);
         message.error("Error instantiate: " + (error.message || error));
      })
   }

   function validateForm(): boolean {

      if (!getInstantiationParameter("instanceName")) {
         // message.error("Please enter a job name.");
         return false;
      }
      for (let field of inputFieldSpec) {
         if (field.disAllowDefault && !getInstantiationParameter(field.valueKey)) {
            // message.error(`Please select a value for ${field.title[Localization.getLanguage() as "da" | "en"]}.`);
            return false;
         }
      }
      return true;
   }


   function getInstantiationParameter(key?: string): any {
      return instantiationParameters[key || ""] || "";
   }

   function setInstantiationParameter(key: string = "", value: any) {
      let newParams = { ...instantiationParameters };
      newParams[key] = value;
      setInstantiationParameters(newParams);
   }

   function filterProjectsByAccessRights(project: JobType): boolean {
      if (hasAccessToFeature(Feature.JobModuleGlobalAdmin)) {
         return true;
      };
      let accessRights = getMyAccessRights(project as any, sessionState.userRef, sessionState.customerRef);
      if (accessRights && accessRights.includes(JobTypeUserAccessRights.Write)) {
         return true;
      }
      return false;
   }

   const SelectProject = (props: { title: string, onSelect: (value: string) => void }) => {
      return (
         <>
            <div className="GlassInputControl" >
               <select onChange={e => {
                  let id = e.target.value;
                  props.onSelect(id);
               }}
                  style={{ width: "auto" }}
                  value={jobsState.selectedJobTypeId}
                  required
               >
                  {jobsState.selectedJobTypeId ? <></> : <option key={"default"} value={""} >{Localization.getText("Vælg jobtype")}</option>}
                  {(jobsState.availableJobTypesList || []).filter(filterProjectsByAccessRights).toSorted((a, b) => (a.name || "").localeCompare(b.name || "")).map((p: JobType, idx: number) => {
                     return (
                        <option key={p.id + idx} value={p.id}>{p.name}</option>
                     );
                  })}
               </select>
            </div>
         </>
      )
   }

   const FormControlElement = (props: { labelElement, controlElement }) => {
      return (
         <GlassTextInputTransformer label={props.labelElement} >
            {props.controlElement}
         </GlassTextInputTransformer>
      )
   }

   if (inputFieldSpec.length === 0 && jobsState.selectedJobType) {
      return (
         <JobInputParamsServer selectedJobType={jobsState.selectedJobType} onCallback={(id) => {
            console.log("Selected job type: " + id);
         }} />
      )
   }

   return (
      <>
         <ADModalBody>
            <GlassInfo>
               {Localization.getText("Vælg de data og indstillinger der skal bruges til beregningen")}
            </GlassInfo>
            <GlassInputGroup autoFocus={true}>
               <GlassTextInputTransformer label={Localization.getText("Jobtype")} >
                  <SelectProject title={Localization.getText("Vælg jobtype")} onSelect={(id) => {
                     jobsDispatch({
                        type: JobActionType.SelectJobType,
                        payload: { id }
                     });
                     let p = jobsState.availableJobTypesList?.filter(filterProjectsByAccessRights).find((a) => a.id === id);
                     if (p) {
                        setProjectId(p.id);
                     }
                  }}
                  />
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Navn på job")}>
                  <input
                     type="text"
                     value={getInstantiationParameter("instanceName")}
                     onChange={(e) => setInstantiationParameter("instanceName", e.target.value)}
                     required
                     minLength={3}
                  />
               </GlassTextInputTransformer>
               {inputFieldSpec.map((field) => {
                  const lang: "da" | "en" = Localization.getLanguage() as "da" | "en";
                  switch (field.type) {
                     case InputFieldType.SelectLayer:
                        return (
                           <Fragment key={"fld" + field.title[lang]}>
                              <FormControlElement
                                 key={"fld" + field.title[lang]}
                                 labelElement={(
                                    <>{field.title[lang]}</>
                                 )}
                                 controlElement={(
                                    <SelectLayer
                                       key={"inp" + field.title[lang]}
                                       availableLayers={availableLayers}
                                       title={field.title[lang][lang]}
                                       selectedLayerId={getInstantiationParameter(field.valueKey)}
                                       onSelect={(layerId: any) => {
                                          setInstantiationParameter(field.valueKey, layerId);
                                       }}
                                    />
                                 )}
                              />
                           </Fragment>
                        );
                     case InputFieldType.Title:
                        return <Fragment key={"fld" + field.title[lang]}><GlassInputGroupTitle>{field.title[lang]}</GlassInputGroupTitle></Fragment>;

                     case InputFieldType.SelectColumn:
                        let options = getInstantiationParameter(field.optionsKey!) || [];
                        return options?.length ? (
                           <Fragment key={"fld" + field.title[lang]}>
                              <FormControlElement
                                 key={"fld" + field.title[lang]}
                                 labelElement={(
                                    <>{field.title[lang]}</>
                                 )}
                                 controlElement={(
                                    <SelectColumn
                                       key={"inp" + field.title[lang]}
                                       disAllowDefault={field.disAllowDefault}

                                       defaultText={Localization.getText("Choose OPT field")}
                                       title={field.title[lang][lang]}
                                       columns={options}
                                       sKey={getInstantiationParameter(field.valueKey)}
                                       onSelect={(columnId: any) => {
                                          setInstantiationParameter(field.valueKey, columnId);
                                       }} />
                                 )}
                              />
                           </Fragment>
                        ) : null;
                     case InputFieldType.Settings:
                        return (
                           <Fragment key={"fld" + field.title[lang]}>
                              <FormControlElement
                                 key={"fld" + field.title[lang]}
                                 labelElement={(
                                    <>{field.title[lang]}</>
                                 )}
                                 controlElement={(
                                    <ViewButtonText
                                       key={"inp" + field.title[lang]}
                                       title={Localization.getText("Settings")}
                                       onClick={() => setShowSettings(true)}
                                    >
                                       {Localization.getText("Åbn")}
                                    </ViewButtonText>
                                 )}
                              />
                           </Fragment>
                        );
                  }
               })
               }

            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            <ProtectedFeature feature={Feature.JobModule} contentsIfNoAccess={<></>}>
               <GlassButton
                  disabled={!validateForm()}

                  onClick={() => {
                     function doIt() {
                        // Collect settings (and defaults)
                        let st: any = {};
                        for (let settingDef of jobInterfaceInstance!.getSettingsForJob(jobsState.selectedJobType?.serverTypeName ?? "Error no job type name").settings) {
                           let key = settingDef.key;
                           let val = SettingsManager.getSystemSetting(key, settingDef.default, true);
                           if (val === undefined && key === "tidsvindue.date") {
                              val = new Date().toISOString().substring(0, 10).split("-").join(""); // Date format is YYYYMMDD
                           }
                           st[key] = val;
                        }
                        runInstantiate(st);
                     }
                     Cookies.getCookieBoolean("RememberJobInstantiationGDPRAccept") ? doIt() : windowDispatch(openWindow(WindowId.GDPRNoticeApproval, {
                        cookie: "RememberJobInstantiationGDPRAccept",
                        acceptText: Localization.getText("JobInstantiatonGDPRAccept"),
                        acceptCallback:
                           () => {
                              doIt();
                           }
                     }));
                  }}>{Localization.getText("Start nyt job")}</GlassButton>
            </ProtectedFeature>
         </ADModalFooter>
         {showSettings ? createPortal(
            <SettingsEditor
               showWindow={1}
               affectedMessageText={Localization.getText("Settings for the job")}
               allSettings={jobInterfaceInstance!.getSettingsForJob(jobsState.selectedJobType?.serverTypeName ?? "Error no job type name").settings}
               callbackOnClose={() => setShowSettings(false)}
               callbackToResetCatchment={() => { throw new Error("Not implemented") }}
               filteredSettingsGroup={jobInterfaceInstance!.getSettingsForJob(jobsState.selectedJobType?.serverTypeName ?? "Error no job type name").settingsGroups}
               priority={settingsFixedPriorities as SettingsFixedPriorities}
               settingsManager={{
                  extractSettingsByPriority: (source: SettingsFixedPriorities, priority: SettingsFixedPriorities, includeDisabled: boolean) => {
                     if ((priority as any) === settingsFixedPriorities) {
                        return (projRef.current || {})
                     }
                     return SettingsManager.extractSettingsByPriority(source, priority, includeDisabled);
                  },
                  clearByPriority: (source: SettingsFixedPriorities) => {
                     SettingsManager.clearByPriority(source);
                  },
                  addSettings: (settings: keyValueObject, { filename, priority }: { filename: string, priority: SettingsFixedPriorities }) => {
                     projRef.current = settings;
                     SettingsManager.addSettings(settings, { filename: filename, priority: priority });
                  },
                  getSystemSetting: (key: string, defaultValue: any, includeDisabled: boolean) => {
                     return (projRef.current || {})[key] ?? SettingsManager.getSystemSetting(key, defaultValue, includeDisabled);
                  },
                  getState: () => { return (projRef.current || {}) },
               }}
               scope={"Project" as any}
               getSource={(key: string): any => {
                  return (projRef.current || {})[key] != undefined ? settingsFixedPriorities : SettingsManager.getSystemSettingSource(key)?.priority
               }}
               callbackOnSaveChanges={() => {
                  setShowSettings(false);
               }}
            />, document.getElementById("Mit-NonBlockingOverlay") || document.body) : null}
      </>

   )
}

